import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { LoggerService } from '../../../services/logger.service';

/**
 * Per-studio QuickBooks Online configuration screen.
 *
 * There is ONE platform-owned Intuit app; studios do not have their own. So
 * this form never asks for (or shows, or posts) a client id or secret. Those
 * are resolved server-side from settings.php. The operator's whole job here is
 * to click Connect and authorize their own QuickBooks company, then pick which
 * accounts we post to.
 *
 * Whether connecting is even possible comes from account-info's app_configured
 * flag, which is why that call is made on init rather than only once connected.
 *
 * Tokens (field_qb_access_token / refresh / realm) are owned entirely by the
 * backend OAuth flow. They are read to derive the connected state but are
 * NEVER posted back by this form.
 */
@Component({
  selector: 'app-form-quickbooks-settings',
  templateUrl: './form-quickbooks-settings.component.html',
  styleUrls: ['./form-quickbooks-settings.component.css'],
})
export class FormQuickbooksSettingsComponent implements OnInit, OnDestroy {
  quickbooksSettingsForm: FormGroup;
  showProgressSpinner = false;
  successMessage: string;
  errorMessage: string;

  /** True when the backend holds an access token for this studio. */
  isConnected = false;
  /** True when the server has Intuit app credentials; gates Connect. */
  appConfigured = false;

  /** Response of /api/quickbooks/oauth/account-info/{studioId}. */
  accountInfo: any = null;
  /** True until the first account-info load resolves; suppresses the
   *  "not configured on the server" message before we actually know. */
  loadingAccountInfo = true;
  /** False until the first settings load resolves; the connection status is
   *  unknown before then, so the template must not render "Not connected". */
  settingsLoaded = false;

  /** Response of /api/quickbooks/status/{studioId}. */
  status: any = null;
  loadingStatus = false;

  syncing = false;
  syncMessage: string;
  syncCounts: { label: string, count: any }[] = [];

  private readonly configFieldName = 'field_quickbooks_ref';

  /** Post-sync polling: stop after ~1 minute or once this studio drains. */
  private pollTimer: any = null;
  private pollTicks = 0;
  private readonly pollIntervalMs = 5000;
  private readonly pollMaxTicks = 12;

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    private _authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.quickbooksSettingsForm = this.fb.group({
      qbEnabled: [false],
      qbIncomeAccount: [''],
      qbPaymentsAccount: [''],
      qbDiscountAccount: [''],
      qbInterestAccount: [''],
    });

    this.readOAuthResult();
    this.loadQuickbooksSettings();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * The backend redirects the browser back to this page with ?authorized=true
   * or ?error=<reason>. Surface it, then strip the params so a refresh doesn't
   * repeat the message.
   */
  private readOAuthResult(): void {
    const params = this.route.snapshot.queryParams;

    if (params['authorized'] === 'true' || params['authorized'] === true) {
      this.successMessage = 'QuickBooks connected successfully.';
    } else if (params['error']) {
      this.errorMessage = 'QuickBooks authorization failed: ' + params['error'];
    } else {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  loadQuickbooksSettings(): void {
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${this.configFieldName}`)
      .subscribe(
        (response: any) => {
          // '1'/'0' string booleans come back from Drupal, so coerce to boolean.
          const enabled = response?.['field_qb_enabled']?.[0]?.value;

          this.quickbooksSettingsForm.patchValue({
            qbEnabled: enabled === true || enabled === '1' || enabled === 1,
            qbIncomeAccount: response?.['field_qb_income_account']?.[0]?.value ?? '',
            qbPaymentsAccount: response?.['field_qb_payments_account']?.[0]?.value ?? '',
            qbDiscountAccount: response?.['field_qb_discount_account']?.[0]?.value ?? '',
            qbInterestAccount: response?.['field_qb_interest_account']?.[0]?.value ?? '',
          });

          this.isConnected = !!response?.['field_qb_access_token']?.[0]?.value;

          if (this.isConnected) {
            this.loadStatus();
          }

          // Always: this is where app_configured comes from, and the Connect
          // button needs it precisely when the studio is NOT yet connected.
          this.loadAccountInfo();
          this.settingsLoaded = true;
        },
        (error) => {
          // A studio with no QuickBooks config yet simply starts blank, but
          // the server may still have app credentials, so ask anyway.
          LoggerService.error('Error loading QuickBooks settings:', error);
          this.loadAccountInfo();
          this.settingsLoaded = true;
        }
      );
  }

  saveQuickbooksSettings(): void {
    // The Intuit app credentials and environment are resolved server-side and
    // are deliberately never posted from here.
    const v = this.quickbooksSettingsForm.getRawValue();

    const postData = {
      config_field_name: this.configFieldName,
      config_data: {
        field_qb_enabled: [{ value: v.qbEnabled ? 1 : 0 }],
        field_qb_income_account: [{ value: v.qbIncomeAccount }],
        field_qb_payments_account: [{ value: v.qbPaymentsAccount }],
        field_qb_discount_account: [{ value: v.qbDiscountAccount }],
        field_qb_interest_account: [{ value: v.qbInterestAccount }],
      },
    };

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService.httpPOST('/api_rest/v1/saveStudioConfig', postData).subscribe(
      () => {
        this.showProgressSpinner = false;
        this.successMessage = 'QuickBooks settings saved.';
      },
      (error) => {
        this.showProgressSpinner = false;
        LoggerService.error('Error saving QuickBooks settings:', error);
        this.errorMessage = 'Error saving QuickBooks settings.';
      }
    );
  }

  onSubmit(): void {
    this.saveQuickbooksSettings();
  }

  authorizeQuickbooks(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.errorMessage = 'No studio selected. Unable to connect to QuickBooks.';
      return;
    }

    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService.httpGET(`/api/quickbooks/oauth/authorize/${studioId}`).subscribe(
      (response: any) => {
        if (response?.url) {
          window.open(response.url, '_self');
        } else {
          this.errorMessage = response?.error || 'Could not start the QuickBooks connection.';
        }
      },
      (error) => {
        LoggerService.error('Error starting QuickBooks authorization:', error);
        this.errorMessage = error?.error?.error || 'Could not start the QuickBooks connection.';
      }
    );
  }

  deauthorizeQuickbooks(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.errorMessage = 'No studio selected. Unable to disconnect.';
      return;
    }

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService.httpPOST(`/api/quickbooks/oauth/deauthorize/${studioId}`, {}).subscribe(
      () => {
        this.showProgressSpinner = false;
        this.successMessage = 'QuickBooks disconnected.';
        this.isConnected = false;
        this.accountInfo = null;
        this.status = null;
        this.syncCounts = [];
        this.syncMessage = '';
        this.stopPolling();
        // appConfigured is a server-side fact and survives disconnecting, but
        // re-read it so Connect reappears in the right state.
        this.loadAccountInfo();
      },
      (error) => {
        this.showProgressSpinner = false;
        LoggerService.error('Error disconnecting QuickBooks:', error);
        this.errorMessage = error?.error?.error || 'Error disconnecting QuickBooks. Please try again.';
      }
    );
  }

  loadAccountInfo(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      return;
    }

    this.loadingAccountInfo = true;

    this.drupalRESTService.httpGET(`/api/quickbooks/oauth/account-info/${studioId}`).subscribe(
      (response: any) => {
        this.loadingAccountInfo = false;
        this.accountInfo = response || null;
        this.appConfigured = response?.app_configured === true;
        // The backend is authoritative on the connected state.
        if (response && response.connected === false) {
          this.isConnected = false;
        }
      },
      (error) => {
        this.loadingAccountInfo = false;
        LoggerService.error('Error loading QuickBooks account info:', error);
      }
    );
  }

  loadStatus(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      return;
    }

    this.loadingStatus = true;

    this.drupalRESTService.httpGET(`/api/quickbooks/status/${studioId}`).subscribe(
      (response: any) => {
        this.loadingStatus = false;
        this.status = response || null;

        // Stop polling early once this studio's own backlog is clear. The
        // global depth is checked too, since an empty queue means nothing is
        // pending for anybody.
        if (this.pollTimer && (this.pendingItems === 0 || this.queueDepth === 0)) {
          this.stopPolling();
        }
      },
      (error) => {
        this.loadingStatus = false;
        LoggerService.error('Error loading QuickBooks status:', error);
      }
    );
  }

  syncNow(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.errorMessage = 'No studio selected. Unable to sync.';
      return;
    }

    this.syncing = true;
    this.syncMessage = '';
    this.syncCounts = [];
    this.errorMessage = '';

    this.drupalRESTService.httpPOST(`/api/quickbooks/sync/${studioId}`, {}).subscribe(
      (response: any) => {
        this.syncing = false;

        // The backend refuses a second backfill while this studio still has
        // queued items. That is the expected answer to an impatient click, not
        // an error. Say so and re-read the status so the button reflects it.
        if (response?.already_queued) {
          this.syncCounts = [];
          this.syncMessage = response.message || 'A sync is already queued for this studio.';
          this.startPolling();
          return;
        }

        this.syncCounts = this.toCounts(response);
        this.syncMessage = this.syncCounts.length
          ? 'Sync queued.'
          : 'Sync queued. Nothing new to send.';
        this.startPolling();
      },
      (error) => {
        this.syncing = false;
        LoggerService.error('Error queuing QuickBooks sync:', error);
        this.errorMessage = error?.error?.error || 'Could not queue the QuickBooks sync. Please try again.';
      }
    );
  }

  /**
   * Turns the sync response into label/count pairs. The exact shape is not
   * guaranteed, so only numeric entries are shown.
   */
  private toCounts(response: any): { label: string, count: any }[] {
    if (!response || typeof response !== 'object') {
      return [];
    }

    return Object.keys(response)
      .filter((key) => typeof response[key] === 'number')
      .map((key) => ({
        label: key.replace(/_/g, ' '),
        count: response[key],
      }));
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollTicks = 0;
    this.loadStatus();

    this.pollTimer = setInterval(() => {
      this.pollTicks++;
      if (this.pollTicks > this.pollMaxTicks) {
        this.stopPolling();
        return;
      }
      this.loadStatus();
    }, this.pollIntervalMs);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /** Defensive accessors: the status payload shape is not guaranteed. */

  /** This studio's own queued items, the number operators care about. */
  get pendingItems(): number {
    const pending = this.status?.pending;
    return typeof pending === 'number' ? pending : 0;
  }

  /** Whether a sync is outstanding, which is what gates the Sync Now button. */
  get syncInProgress(): boolean {
    return this.pendingItems > 0;
  }

  get queueDepth(): number {
    const depth = this.status?.queue_depth_all_studios;
    return typeof depth === 'number' ? depth : 0;
  }

  get unsyncedCustomers(): number {
    return this.status?.unsynced?.customers ?? 0;
  }

  get unsyncedInvoices(): number {
    return this.status?.unsynced?.invoices ?? 0;
  }

  get unsyncedPayments(): number {
    return this.status?.unsynced?.payments ?? 0;
  }

  get lastError(): string {
    return this.status?.last_error || '';
  }
}
